import { create } from 'zustand';
import { useConnectionStore } from './connection';
import { useWalletEnvStore } from './wallet-env';
import { DEVICE_TYPE, MARKETPLACE_CHAINS } from '@shared/types/enums';
import { errorDict } from './ledger';
import { useApiServiceStore } from './api';
import { WalletDeviceApi } from '@shared/types/api';

interface Wallet {
  signTransaction(path: string, unsignedTx: any): Promise<WalletDeviceApi.Sign>;
}

interface WalletState {
  walletType: 'ledger' | 'trezor' | 'walletconnect';
  wallet: Wallet | undefined;
  devicePath: string | undefined;
  setWalletType: (type: 'ledger' | 'trezor' | 'walletconnect') => void;
  initializeWallet: () => Promise<void>;
  isLedger: () => boolean;
  signTransaction: (unsignedTx: any) => Promise<WalletDeviceApi.Sign>;
}

export const useWalletStore = create<WalletState>((set, get) => {
  class WalletConnectWallet implements Wallet {
    async signTransaction(path: string, unsignedTx: any): Promise<WalletDeviceApi.Sign> {
      const { isConnected, addressEWX, session, client } = useConnectionStore.getState();
      const { walletConst } = useWalletEnvStore.getState();

      if (!client || !addressEWX) {
        throw new Error('Client not initialized');
      }

      if (!isConnected || !session) {
        throw new Error('Session is not connected');
      }

      const result: WalletDeviceApi.Sign = await client.request({
        chainId: `polkadot:${walletConst.ewxChainId}`,
        topic: session.topic,
        request: {
          method: 'polkadot_signTransaction',
          params: {
            address: addressEWX,
            transactionPayload: unsignedTx,
          },
        },
      });

      return result;
    }
  }

  class LedgerWallet implements Wallet {
    async signTransaction(path: string, unsignedTx: any): Promise<WalletDeviceApi.Sign> {
      try {
        const workerApi = useApiServiceStore.getState().api;
        return await workerApi.deviceSign(
          DEVICE_TYPE.Ledger,
          MARKETPLACE_CHAINS.EWX,
          JSON.stringify(unsignedTx),
          path,
        );
      } catch (error) {
        if (error instanceof Error) {
          for (const [key, value] of Object.entries(errorDict)) {
            if (error.message.includes(key)) {
              throw new Error(value);
            }
          }
        }

        throw new Error('Error signing transaction');
      }
    }
  }

  return {
    walletType: 'walletconnect', // Default wallet type
    wallet: undefined,
    devicePath: undefined,
    isLedger: () => {
      const { walletType } = get();
      return walletType === 'ledger';
    },
    setWalletType: async (type: 'ledger' | 'trezor' | 'walletconnect') => {
      set({ walletType: type });
      await get().initializeWallet();
    },
    initializeWallet: async () => {
      const { walletType } = get();
      let wallet: Wallet | undefined = undefined;
      if (walletType === 'ledger') {
        wallet = new LedgerWallet();
      } else if (walletType === 'walletconnect') {
        wallet = new WalletConnectWallet();
      } else {
        throw new Error('Unsupported wallet type');
      }
      set({ wallet });
    },
    signTransaction: async (unsignedTx: any) => {
      const { wallet, devicePath } = get();
      if (!wallet) throw new Error('Wallet not initialized');
      const signature = await wallet.signTransaction(devicePath!, unsignedTx);
      return signature;
    },
  };
});
